import { create } from "zustand";
import { devtools } from "zustand/middleware";
import pipelinesService from "@/services/pipelines.service";
import dealsService from "@/services/deals.service";
import { TDealPipeline, TDealStatus } from "@/models/deals.model";
import { TRequestStatus } from "@/models/msc.model";

type PipelinesState = {
    initialized: boolean;
    entities: Record<number, TDealPipeline>;
    ids: number[];
    currentPipelineId: number;
    ui: {
        status: TRequestStatus;
    };
};

type PipelinesActions = {
    getAllPipelines: () => Promise<void>;
    setCurrentPipeline: (pipelineId: number) => void;
    changeCurrentPipeline: (pipelineId: number) => void;
    createDealStatus: (payload: {
        pipelineId: number;
        name: string;
        color: string;
        isClosedStatus?: boolean;
    }) => Promise<TDealStatus>;
    updateDealStatus: (payload: {
        pipelineId: number;
        statusId: number;
        name: string;
        color: string;
        isClosedStatus: boolean;
    }) => Promise<TDealStatus>;
    deleteDealStatus: (payload: {
        pipelineId: number;
        statusId: number;
        newStatusId?: number;
    }) => Promise<void>;
    createPipeline: (pipelineName: string) => Promise<TDealPipeline>;
    updatePipeline: (pipelineId: number, pipelineName: string) => Promise<TDealPipeline>;
    deletePipeline: (pipelineId: number) => Promise<void>;
    resetPipelines: () => void;
};

const initialState: PipelinesState = {
    initialized: false,
    entities: {},
    ids: [],
    currentPipelineId: 0,
    ui: {
        status: "IDLE",
    },
};

// Immutable helper: replace one pipeline's status array.
const withPipelineStatus = (
    state: PipelinesState,
    pipelineId: number,
    mapStatus: (status: TDealStatus[]) => TDealStatus[]
): Partial<PipelinesState> => {
    const pipeline = state.entities[pipelineId];
    if (!pipeline) return {};
    return {
        entities: {
            ...state.entities,
            [pipelineId]: { ...pipeline, status: mapStatus(pipeline.status) },
        },
    };
};

export const usePipelinesStore = create<PipelinesState & PipelinesActions>()(
    devtools(
        (set, get) => ({
            ...initialState,

            getAllPipelines: async () => {
                const { initialized } = get();
                if (initialized) return;

                set({ ui: { status: "PENDING" } }, false, "getAllPipelines/pending");
                const response = await pipelinesService.getAll();

                set(
                    (state) => {
                        const entities: Record<number, TDealPipeline> = {};
                        const ids: number[] = [];
                        response.forEach((pipeline) => {
                            entities[pipeline.id] = pipeline;
                            ids.push(pipeline.id);
                        });

                        // if no current pipeline, use the first one
                        const currentPipelineId =
                            !state.currentPipelineId && ids.length > 0
                                ? ids[0]
                                : state.currentPipelineId;

                        return {
                            entities,
                            ids,
                            currentPipelineId,
                            initialized: true,
                            ui: { status: "IDLE" as TRequestStatus },
                        };
                    },
                    false,
                    "getAllPipelines/fulfilled"
                );
            },

            setCurrentPipeline: (pipelineId) =>
                set({ currentPipelineId: pipelineId }, false, "setCurrentPipeline"),

            changeCurrentPipeline: (pipelineId) => get().setCurrentPipeline(pipelineId),

            createDealStatus: async (payload) => {
                const response = await dealsService.saveDealStatus(payload);
                set(
                    (state) =>
                        withPipelineStatus(state, payload.pipelineId, (status) => [...status, response]),
                    false,
                    "createDealStatus/fulfilled"
                );
                return response;
            },

            updateDealStatus: async (payload) => {
                const { statusId, name, color, isClosedStatus, pipelineId } = payload;
                const response = await dealsService.updateDealStatus({
                    id: statusId,
                    name,
                    color,
                    isClosedStatus,
                } as TDealStatus);
                set(
                    (state) =>
                        withPipelineStatus(state, pipelineId, (status) =>
                            status.map((s) => (s.id === response.id ? response : s))
                        ),
                    false,
                    "updateDealStatus/fulfilled"
                );
                return response;
            },

            deleteDealStatus: async (payload) => {
                await dealsService.deleteDealStatus(payload.statusId, payload.newStatusId);
                set(
                    (state) =>
                        withPipelineStatus(state, payload.pipelineId, (status) =>
                            status.filter((s) => s.id !== payload.statusId)
                        ),
                    false,
                    "deleteDealStatus/fulfilled"
                );
            },

            createPipeline: async (pipelineName) => {
                const newPipeline = await pipelinesService.save(pipelineName);
                set(
                    (state) => ({
                        entities: { ...state.entities, [newPipeline.id]: newPipeline },
                        ids: [...state.ids, newPipeline.id],
                    }),
                    false,
                    "createPipeline/fulfilled"
                );
                return newPipeline;
            },

            updatePipeline: async (pipelineId, pipelineName) => {
                const updatedPipeline = await pipelinesService.updateById(pipelineId, pipelineName);
                set(
                    (state) => {
                        const existing = state.entities[updatedPipeline.id];
                        if (!existing) return {};
                        // Merge the updated data with existing pipeline to preserve status array
                        return {
                            entities: {
                                ...state.entities,
                                [updatedPipeline.id]: { ...existing, ...updatedPipeline },
                            },
                        };
                    },
                    false,
                    "updatePipeline/fulfilled"
                );
                return updatedPipeline;
            },

            deletePipeline: async (pipelineId) => {
                await pipelinesService.deleteById(pipelineId);
                set(
                    (state) => {
                        const entities = { ...state.entities };
                        delete entities[pipelineId];
                        const ids = state.ids.filter((id) => id !== pipelineId);

                        // If we deleted the current pipeline, switch to the first available one
                        const currentPipelineId =
                            state.currentPipelineId === pipelineId && ids.length > 0
                                ? ids[0]
                                : state.currentPipelineId;

                        return { entities, ids, currentPipelineId };
                    },
                    false,
                    "deletePipeline/fulfilled"
                );
            },

            resetPipelines: () => set(initialState, false, "resetPipelines"),
        }),
        { name: "pipelines" }
    )
);
